import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, first, map } from 'rxjs/operators';
import { ButtonInformation, ElementRect, IAppButtonInfo, IDeviceContext, IDeviceContextHandler, ISkinInformation } from '../mcrc-api';
import { RcStore } from './rcStore';
import { MathHelpers } from '../utils/math';
import { RcContext } from '../context/rcContext';
import { DeviceContext, IDeviceContextScreen } from '../context/deviceContext';
import { VideoRecordingSetupMessage } from '../messages/xsight';
import { cloneDeep } from 'lodash';

@Injectable({
    providedIn: 'root'
})
export class SkinInfoService {
    private SKIN_LAST_DOWNLOAD_TIME_KEY = 'skinLastDownloaded';
    private SEVEN_DAYS_IN_MILLISECONDS = 604800000;

    private xmlHttp: XMLHttpRequest;
    private ourContext: DeviceContext;
    private contextHandler;
    public deviceName: string;

    constructor(
        private localStorage: RcStore,
    ) { }

    private parseButtonInfo(buttonsTag: any[], buttonIndexes: ButtonInformation[], applicationButtonIndexes: IAppButtonInfo[]): void {
        let applicationButtonIndex = 0;

        for (let buttonIdx = 0; buttonIdx < buttonsTag.length; buttonIdx++) {
            const buttonTag = buttonsTag[buttonIdx];
            const buttonInfo = buttonTag.area.split(',');
            const virtualkey = buttonTag.key;
            const keyType = virtualkey.substring(0, 2);
            let keyValue = parseInt(virtualkey.substring(2));
            if (isNaN(keyValue)) {
                keyValue = -1;
            }

            const button = {
                id: buttonTag.id,
                left: parseInt(buttonInfo[0]),
                top: parseInt(buttonInfo[1]),
                right: parseInt(buttonInfo[2]),
                bottom: parseInt(buttonInfo[3]),
                type: keyType,
                keyValue: keyValue,
            } as ButtonInformation;
            buttonIndexes[buttonIdx] = button;
            if (keyType === 'AK') {
                const appButton = {
                    id: buttonTag.id,
                    type: keyType,
                    keyValue: keyValue,
                    text: buttonTag.text
                } as IAppButtonInfo;
                applicationButtonIndexes[applicationButtonIndex] = appButton;

                applicationButtonIndex++;
            }
        }
    }

    private parseJSON(json: any, ourContext: DeviceContext): Observable<ISkinInformation> {
        if (json.screen === null) {
            return of(null);
        }

        const buttonIndexes: ButtonInformation[] = [];
        const applicationButtonIndexes: IAppButtonInfo[] = [];

        const buttonsTag = json.buttons;
        this.parseButtonInfo(buttonsTag, buttonIndexes, applicationButtonIndexes);
        const skinInfo: ISkinInformation = <ISkinInformation>{};
        skinInfo.id = json.id;
        skinInfo.screen = <ElementRect>{};
        skinInfo.deviceWidth = json.imageWidth;
        skinInfo.deviceHeight = json.imageHeight;
        skinInfo.rotation = json.rotation;
        skinInfo.hasNotchImage = json.hasNotchImage;

        if (json.screen != null) {
            var screenInfo = json.screen.split(',');
            skinInfo.screen.top = parseInt(screenInfo[0]);
            skinInfo.screen.left = parseInt(screenInfo[1]);
            skinInfo.screen.bottom = parseInt(screenInfo[2]);
            skinInfo.screen.right = parseInt(screenInfo[3]);
        }

        // json.skin3d has been removed from the code as DeviceCanvas3D is not used
        const screenTmp = cloneDeep(skinInfo.screen)
        MathHelpers.applyRotation(screenTmp, skinInfo.deviceWidth, skinInfo.deviceHeight, skinInfo.rotation);

        let h = screenTmp.bottom - screenTmp.top;
        let w = screenTmp.right - screenTmp.left;
        const rotate = (ourContext.deviceWidth > ourContext.deviceHeight && w < h) ||
            (ourContext.deviceWidth < ourContext.deviceHeight && w > h);
        return ourContext.rotation$.pipe(first(r => !rotate || r !== undefined), map(rotation => {
            // Possible skin rotation mismatch if this ends up being true, rotate by 90 to fix
            if (rotate) {
                skinInfo.rotation = (skinInfo.rotation + 1) & 3;
            }

            if (skinInfo.screen.top !== undefined) {
                ourContext.screen = cloneDeep(skinInfo.screen) as IDeviceContextScreen;
                ourContext.screen.rotation = skinInfo.rotation;
                ourContext.screen.dw = skinInfo.deviceWidth;
                ourContext.screen.dh = skinInfo.deviceHeight;
            } else {
                // Define our screen based on known information
                ourContext.screen = <IDeviceContextScreen>{};
                ourContext.screen.top = 0;
                ourContext.screen.left = 0;
                ourContext.screen.right = ourContext.deviceWidth;
                ourContext.screen.bottom = ourContext.deviceHeight;
                ourContext.screen.dw = ourContext.deviceWidth;
                ourContext.screen.dh = ourContext.deviceHeight;
                ourContext.screen.rotation = 0;
            }
            const deviceToSend: ISkinInformation = Object.assign({ name: '' }, cloneDeep(skinInfo));
            MathHelpers.applyRotation(skinInfo.screen, skinInfo.deviceWidth, skinInfo.deviceHeight, skinInfo.rotation);
            skinInfo.buttonIndexes = buttonIndexes;
            skinInfo.applicationButtonIndexes = applicationButtonIndexes;
            for (let i = 0; i < skinInfo.buttonIndexes.length; i++) {
                MathHelpers.applyRotation(<any>skinInfo.buttonIndexes[i] as ElementRect, skinInfo.deviceWidth, skinInfo.deviceHeight, skinInfo.rotation);
            }

            if (deviceToSend && deviceToSend.id) {
                RcContext.instance.session.sendMessage(new VideoRecordingSetupMessage(deviceToSend));
            }
            return skinInfo;
        }));
    }

    private isLocalStoredSkinExpired(): boolean {
        let result = false;
        const currentDate = new Date().getTime();

        var localStoreDate = this.localStorage.loadString(this.SKIN_LAST_DOWNLOAD_TIME_KEY);

        if (localStoreDate != null) {
            result = ((currentDate - new Date(localStoreDate).getTime()) >= this.SEVEN_DAYS_IN_MILLISECONDS);
        }

        return result;
    }

    public loadButtonInfo(
        deviceName: string,
        callback: (skinInfo: ISkinInformation) => void,
        context: DeviceContext,
        deviceContextHandler: IDeviceContextHandler
    ): void {
        this.deviceName = deviceName;
        if (!this.deviceName) {
            callback(null);
            return;
        }

        this.ourContext = context;
        const localStoreKey = 'info.' + this.deviceName;
        const localButtonInfo = this.localStorage.loadString(localStoreKey);

        if (localButtonInfo === null || this.isLocalStoredSkinExpired()) {
            this.xmlHttp = new XMLHttpRequest();
            this.xmlHttp.onreadystatechange = () => {
                this.httpOnReadyStateChange(this.xmlHttp, callback);
            };
            if (this.xmlHttp.overrideMimeType !== undefined) this.xmlHttp.overrideMimeType('application/json');
            this.xmlHttp.open('GET', 'https://skinsapi.soti.net/api/skins/' + this.deviceName + '/', true);
            this.xmlHttp.responseType = 'json';
            this.xmlHttp.setRequestHeader('If-Modified-Since', 'Sat, 1 Jan 2005 00:00:00 GMT');
            this.xmlHttp.setRequestHeader('Accept', 'text/html,application/json,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8');
            this.xmlHttp.setRequestHeader('Access-Control-Allow-Origin', '*');
            this.xmlHttp.send();
        } else {
            const json = JSON.parse(localButtonInfo);
            this.parseJSON(json, this.ourContext)
                .pipe(
                    catchError((error: any) => {
                        console.error('Error parsing JSON:', error);
                        return of(null);
                    })
                )
                .subscribe((skinInfo) => {
                    callback(skinInfo);
                });
        }
    }

    private httpOnReadyStateChange(xmlHttp: XMLHttpRequest, callback: (skinInfo: any) => void): void {
        if (xmlHttp.status === 200 && xmlHttp.readyState === XMLHttpRequest.DONE) {
            let httpResponse = xmlHttp.response === undefined ? xmlHttp.responseText : xmlHttp.response;

            if (this.localStorage.supportsHtml5Storage()) {
                const localStoreKey = 'info.' + this.deviceName;
                if (typeof httpResponse !== 'object') {
                    this.localStorage.storeString(localStoreKey, httpResponse);
                } else {
                    this.localStorage.storeString(localStoreKey, JSON.stringify(httpResponse));
                }

                this.localStorage.storeString(this.SKIN_LAST_DOWNLOAD_TIME_KEY, new Date().getTime().toString());
            }

            let json = httpResponse;
            if (typeof httpResponse !== 'object') json = JSON.parse(httpResponse);
            this.parseJSON(json, this.ourContext)
                .pipe(
                    catchError((error: any) => {
                        console.error('Error parsing JSON:', error);
                        return of(null);
                    })
                )
                .subscribe((skinInfo) => {
                    callback(skinInfo);
                });
        } else if (xmlHttp.status === 0 && xmlHttp.readyState === XMLHttpRequest.DONE) {
            callback({});
        }
    }
}
